import React from 'react';
import { GlobalStyles, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import ContrastIcon from '@mui/icons-material/Contrast';

import { usePreferences } from '../preferences';
import { KNOWLEDGE_REVIEW_STAFF_EMAILS } from '../settings/knowledgeReview/knowledgeReviewApi';
import { ContextStore } from '../store/contextStore';
import lightTheme, { buildTheme } from '../theme';
import { cv, varDeclarations } from '../themeVars';

// The chat theme is a per-user preference that only applies on the chat pages.
// App.js mounts this once above the route Switch (so MainLayout, the app bar and
// side menu, sit inside it and follow the theme), and it applies a dark theme
// only while the URL is under /chat: every other page keeps the light theme.
//   light - the regular dashboard look
//   dark  - near-black surfaces, high contrast
//   dim   - muted grey surfaces and softer text, like an IDE "dimmed" theme
//
// Behind the operator config flag `chat_themes_enabled` (platform-managed: set
// from the shell / ops console, never from the dashboard). Unset or false = the
// feature does not exist: no header button, and chat renders light whatever the
// user saved. "staff" = only the staff accounts (same list as the AI training
// inbox), so it can be tried on production first. true = everyone at the operator.
export const CHAT_THEMES_FLAG = 'chat_themes_enabled';
const STAFF_ONLY = 'staff';

export const chatThemesEnabledFor = (tourOperator, user) => {
    const flag = tourOperator?.config?.[CHAT_THEMES_FLAG];
    const email = typeof user?.email === 'string' ? user.email.trim().toLowerCase() : null;
    return flag === true || (flag === STAFF_ONLY && KNOWLEDGE_REVIEW_STAFF_EMAILS.includes(email));
};

export const CHAT_THEME_PREF = 'chat:theme';
// First local build stored a boolean under this key; still honored when unset.
const LEGACY_DARK_MODE_PREF = 'chat:darkMode';

export const CHAT_THEME_OPTIONS = [
    { mode: 'light', label: 'Light', Icon: LightModeOutlinedIcon },
    { mode: 'dark', label: 'Dark', Icon: DarkModeOutlinedIcon },
    { mode: 'dim', label: 'Dimmed', Icon: ContrastIcon },
];

const themes = { light: lightTheme, dark: buildTheme('dark'), dim: buildTheme('dim') };

// Light hands the outer theme through untouched (a function theme returns it as is),
// so every page outside a dark chat sees the exact theme object it always had. An
// object here would be merged into a new copy, and makeStyles keys sheets by identity.
const passThrough = (outerTheme) => outerTheme;

export const ChatThemeContext = React.createContext({ enabled: false, mode: 'light', setMode: () => {} });

export const useChatTheme = () => React.useContext(ChatThemeContext);

const sharedDarkRules = {
    body: {
        backgroundColor: 'var(--cp-bg)',
        color: 'var(--cp-text)',
    },
    // Plain anchors (linkified message text, notes) default to #0000EE.
    'a:not([class])': {
        color: 'var(--cp-accent)',
    },
    // ...except inside the user's own blue bubble, where the light accent would vanish.
    '[data-own-message] a:not([class])': {
        color: 'inherit',
    },
    // Menu icons are Figma exports with a hardcoded #344054 fill/stroke.
    '[fill="#344054"]': { fill: 'var(--cp-text-secondary)' },
    '[stroke="#344054"]': { stroke: 'var(--cp-text-secondary)' },
    // Disabled-state chat icons pass rgba(0, 0, 0, 0.26) as a fill attribute.
    '[fill="rgba(0, 0, 0, 0.26)"], [fill="#00000042"]': { fill: 'var(--cp-text-disabled)' },
};

const globalStylesFor = (mode) => ({
    ':root': {
        ...varDeclarations(mode),
        colorScheme: 'dark', // native scrollbars, form controls, autofill
    },
    ...sharedDarkRules,
});

const globalStyles = { dark: globalStylesFor('dark'), dim: globalStylesFor('dim') };

const ChatThemeScope = ({ children }) => {
    const { getPreference, setPreference } = usePreferences();
    const { tourOperator, user } = React.useContext(ContextStore);
    const { pathname } = useLocation();
    const enabled = chatThemesEnabledFor(tourOperator, user);
    const onChat = pathname === '/chat' || pathname.startsWith('/chat/');
    const stored = getPreference(CHAT_THEME_PREF, null);
    const saved = themes[stored] ? stored : (getPreference(LEGACY_DARK_MODE_PREF, false) === true ? 'dark' : 'light');
    const mode = enabled && onChat ? saved : 'light';

    const value = React.useMemo(
        () => ({ enabled, mode, setMode: (next) => setPreference(CHAT_THEME_PREF, next) }),
        [enabled, mode, setPreference]
    );

    // Keep the tree shape identical in every mode: swapping wrappers would remount
    // the whole chat (Twilio client, open conversation, scroll position) on change.
    return (
        <ChatThemeContext.Provider value={value}>
            <ThemeProvider theme={mode === 'light' ? passThrough : themes[mode]}>
                {mode !== 'light' && <GlobalStyles styles={globalStyles[mode]} />}
                {children}
            </ThemeProvider>
        </ChatThemeContext.Provider>
    );
};

// Header button + menu for picking the chat theme.
export const ChatThemeMenuButton = () => {
    const { enabled, mode, setMode } = useChatTheme();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const current = CHAT_THEME_OPTIONS.find((o) => o.mode === mode) || CHAT_THEME_OPTIONS[0];

    if (!enabled) {
        return null;
    }

    return (
        <>
            <Tooltip title="Chat theme" arrow placement="top">
                <IconButton
                    size="small"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    aria-label="Chat theme"
                    aria-haspopup="true"
                    aria-expanded={anchorEl ? 'true' : undefined}
                    sx={{ color: cv('text-secondary', '#666') }}
                >
                    <current.Icon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
                {CHAT_THEME_OPTIONS.map(({ mode: optionMode, label, Icon }) => (
                    <MenuItem
                        key={optionMode}
                        dense
                        selected={optionMode === mode}
                        onClick={() => {
                            setAnchorEl(null);
                            if (optionMode !== mode) setMode(optionMode);
                        }}
                    >
                        <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                        <ListItemText>{label}</ListItemText>
                        {optionMode === mode && <CheckIcon fontSize="small" sx={{ ml: 2 }} />}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default ChatThemeScope;
